(() => {
  if (customElements.get('us-product-catalogue')) return;

  const pad = (n) => String(n).padStart(2, '0');

  customElements.define('us-product-catalogue', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.signal = signal;
      this.classList.add('us-cat--js');

      this.chapters = [...this.querySelectorAll('[data-catalogue-chapter]')];
      this.tiles = [...this.querySelectorAll('[data-tile]')];
      this.railLinks = [...this.querySelectorAll('[data-chapter-link]')];
      this.sheetLinks = [...this.querySelectorAll('[data-sheet-link]')];
      this.filters = [...this.querySelectorAll('[data-category][aria-pressed]')];
      this.search = this.querySelector('[data-catalogue-search]');
      this.sheetSearch = this.querySelector('[data-sheet-search]');
      this.listed = this.querySelector('[data-catalogue-listed]');
      this.sheetListed = this.querySelector('[data-sheet-listed]');
      this.bar = this.querySelector('[data-catalogue-bar]');
      this.progress = this.querySelector('[data-progress]');
      this.fab = this.querySelector('.us-cat__fab');
      this.sheet = this.querySelector('[data-sheet]');
      this.viewer = this.querySelector('[data-viewer]');
      this.category = 'all';
      this.query = '';
      this.listedOnly = false;
      this.searchText = new Map(this.chapters.map((chapter) => [chapter, chapter.textContent.replace(/\s+/g, ' ').toLowerCase()]));

      this.querySelectorAll('[data-catalogue-tools], [data-open-sheet], [data-open-viewer], .us-cat__fab').forEach((el) => { el.hidden = false; });

      this.measureHeader();
      window.addEventListener('resize', () => this.measureHeader(), { signal, passive: true });

      this.filters.forEach((button) => button.addEventListener('click', () => {
        this.category = button.dataset.category;
        this.update();
        button.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }, { signal }));
      [this.search, this.sheetSearch].forEach((input) => input?.addEventListener('input', () => {
        this.query = input.value;
        if (this.search && this.search !== input) this.search.value = input.value;
        if (this.sheetSearch && this.sheetSearch !== input) this.sheetSearch.value = input.value;
        this.update();
      }, { signal }));
      [this.listed, this.sheetListed].forEach((box) => box?.addEventListener('change', () => {
        this.listedOnly = box.checked;
        if (this.listed) this.listed.checked = box.checked;
        if (this.sheetListed) this.sheetListed.checked = box.checked;
        this.update();
      }, { signal }));
      this.querySelectorAll('[data-catalogue-reset]').forEach((button) => button.addEventListener('click', () => {
        this.resetFilters();
        (this.search || this.sheetSearch)?.focus();
      }, { signal }));

      this.setupSheet();
      this.setupViewer();
      this.setupReveal();
      this.setupSpy();

      window.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
      window.addEventListener('hashchange', () => this.revealHash(), { signal });
      this.update();
      this.onScroll();
      this.revealHash();
    }

    /* The store header may be sticky, hide on scroll, or scroll away entirely; track its live bottom edge. */
    measureHeader() {
      const header = document.querySelector('.bs-header, sticky-header, .shopify-section-header-sticky');
      const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      if (bottom !== this.headerBottom) {
        this.headerBottom = bottom;
        this.style.setProperty('--us-cat-top', `${bottom}px`);
      }
    }

    resetFilters() {
      this.category = 'all';
      this.query = '';
      this.listedOnly = false;
      if (this.search) this.search.value = '';
      if (this.sheetSearch) this.sheetSearch.value = '';
      if (this.listed) this.listed.checked = false;
      if (this.sheetListed) this.sheetListed.checked = false;
      this.update();
    }

    update() {
      const terms = this.query.trim().toLowerCase().split(/\s+/).filter(Boolean);
      const visible = new Set();
      const categoryCounts = { all: 0 };
      this.chapters.forEach((chapter) => {
        const slug = chapter.dataset.catalogueChapter;
        const textMatch = terms.every((term) => this.searchText.get(chapter).includes(term));
        const listedMatch = !this.listedOnly || chapter.dataset.listed === 'true';
        if (textMatch && listedMatch) {
          categoryCounts.all += 1;
          categoryCounts[chapter.dataset.category] = (categoryCounts[chapter.dataset.category] || 0) + 1;
        }
        const matches = textMatch && listedMatch && (this.category === 'all' || chapter.dataset.category === this.category);
        chapter.hidden = !matches;
        if (matches) visible.add(slug);
      });
      this.tiles.forEach((tile) => { tile.hidden = !visible.has(tile.dataset.tile); });
      this.railLinks.forEach((link) => { link.hidden = !visible.has(link.dataset.chapterLink); });
      this.sheetLinks.forEach((link) => { link.hidden = !visible.has(link.dataset.sheetLink); });
      this.querySelectorAll('[data-overview-group]').forEach((group) => {
        group.hidden = ![...group.querySelectorAll('[data-tile]')].some((tile) => !tile.hidden);
      });
      this.querySelectorAll('[data-contents-group]').forEach((group) => {
        group.hidden = ![...group.querySelectorAll('[data-chapter-link]')].some((link) => !link.hidden);
      });
      this.querySelectorAll('[data-sheet-group]').forEach((group) => {
        group.hidden = ![...group.querySelectorAll('[data-sheet-link]')].some((link) => !link.hidden);
      });
      this.filters.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.category === this.category));
        const count = button.querySelector('[data-filter-count]');
        if (count) count.textContent = categoryCounts[button.dataset.category] || 0;
      });
      const total = this.chapters.length;
      const countEl = this.querySelector('[data-catalogue-count]');
      if (countEl) countEl.textContent = visible.size === total ? `All ${total} products` : `${visible.size} of ${total} products`;
      const empty = this.querySelector('[data-catalogue-empty]');
      if (empty) empty.hidden = visible.size !== 0;
      const sheetEmpty = this.querySelector('[data-sheet-empty]');
      if (sheetEmpty) sheetEmpty.hidden = visible.size !== 0;
    }

    /* Sticky bar progress + floating chapter button */
    onScroll() {
      this.measureHeader();
      const rect = this.getBoundingClientRect();
      const cover = this.querySelector('.us-cat__cover');
      const coverBottom = cover ? cover.getBoundingClientRect().bottom : 0;
      if (this.fab) this.fab.classList.toggle('is-visible', coverBottom < 0 && rect.bottom > window.innerHeight * 0.6);
      if (this.progress) {
        const start = this.querySelector('.us-cat__overview')?.getBoundingClientRect().top ?? rect.top;
        const end = rect.bottom;
        const span = Math.max(1, end - start);
        const ratio = Math.min(1, Math.max(0, (window.innerHeight * 0.5 - start) / span));
        this.progress.style.transform = `scaleX(${ratio})`;
      }
    }

    setupReveal() {
      const targets = [...this.querySelectorAll('[data-reveal]')];
      if (!('IntersectionObserver' in window)) { targets.forEach((el) => el.classList.add('is-in')); return; }
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add('is-in'); this.revealObserver.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      targets.forEach((el) => this.revealObserver.observe(el));
    }

    setupSpy() {
      if (!('IntersectionObserver' in window)) return;
      this.spyObserver = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (!visible.length) return;
        const slug = visible[0].target.dataset.catalogueChapter;
        [...this.railLinks, ...this.sheetLinks].forEach((link) => {
          const match = (link.dataset.chapterLink || link.dataset.sheetLink) === slug;
          if (match) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      }, { rootMargin: '-160px 0px -55% 0px', threshold: 0 });
      this.chapters.forEach((chapter) => this.spyObserver.observe(chapter));
    }

    /* Chapter sheet (mobile bottom sheet / desktop modal) */
    setupSheet() {
      const { signal } = this;
      if (!this.sheet || typeof this.sheet.showModal !== 'function') {
        this.querySelectorAll('[data-open-sheet]').forEach((el) => { el.hidden = true; });
        if (this.fab) this.fab.hidden = true;
        return;
      }
      this.querySelectorAll('[data-open-sheet]').forEach((button) => button.addEventListener('click', () => {
        this.openDialog(this.sheet);
        if (button.hasAttribute('data-focus-search')) requestAnimationFrame(() => this.sheetSearch?.focus());
      }, { signal }));
      this.sheetLinks.forEach((link) => link.addEventListener('click', (event) => {
        event.preventDefault();
        const id = decodeURIComponent(link.getAttribute('href').slice(1));
        this.closeDialog(this.sheet);
        const target = this.chapters.find((chapter) => chapter.id === id);
        if (!target) return;
        history.replaceState(null, '', `#${id}`);
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }, { signal }));
      this.bindDialogClose(this.sheet);
    }

    /* Original page reader */
    setupViewer() {
      const { signal } = this;
      const data = this.querySelector('[data-catalogue-pages]');
      const openers = [...this.querySelectorAll('[data-open-viewer]')];
      if (!this.viewer || !data || typeof this.viewer.showModal !== 'function') {
        openers.forEach((el) => { if (el.tagName === 'BUTTON') el.hidden = true; });
        return;
      }
      try { this.pages = JSON.parse(data.textContent); } catch { this.pages = []; }
      if (!this.pages.length) { openers.forEach((el) => { if (el.tagName === 'BUTTON') el.hidden = true; }); return; }

      this.viewerImage = this.viewer.querySelector('[data-viewer-image]');
      this.viewerFrame = this.viewer.querySelector('[data-viewer-frame]');
      this.viewerTitle = this.viewer.querySelector('[data-viewer-title]');
      this.viewerCounter = this.viewer.querySelector('[data-viewer-counter]');
      this.viewerFull = this.viewer.querySelector('[data-viewer-full]');
      this.viewerPrev = this.viewer.querySelector('[data-viewer-prev]');
      this.viewerNext = this.viewer.querySelector('[data-viewer-next]');
      const strip = this.viewer.querySelector('[data-viewer-strip]');
      strip.innerHTML = '';
      this.thumbs = this.pages.map((page, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'us-cat__viewer-thumb';
        button.setAttribute('aria-label', `${page.title}, page ${page.label}`);
        button.innerHTML = `<img alt="" loading="lazy" width="1203" height="1700"><span>${page.label}</span>`;
        button.querySelector('img').dataset.src = page.src;
        button.addEventListener('click', () => this.showPage(index), { signal });
        strip.appendChild(button);
        return button;
      });

      openers.forEach((el) => el.addEventListener('click', (event) => {
        event.preventDefault();
        const number = Number(el.dataset.openViewer);
        const index = Math.max(0, this.pages.findIndex((page) => page.p === number));
        this.openDialog(this.viewer);
        this.showPage(index);
      }, { signal }));
      this.viewerPrev.addEventListener('click', () => this.showPage(this.pageIndex - 1), { signal });
      this.viewerNext.addEventListener('click', () => this.showPage(this.pageIndex + 1), { signal });
      this.viewerImage.addEventListener('click', () => {
        this.viewerFrame.classList.toggle('is-zoomed');
        if (!this.viewerFrame.classList.contains('is-zoomed')) this.viewerFrame.scrollTo({ top: 0 });
      }, { signal });
      this.viewer.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.showPage(this.pageIndex + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.showPage(this.pageIndex - 1); }
        if (event.key === 'Home') { event.preventDefault(); this.showPage(0); }
        if (event.key === 'End') { event.preventDefault(); this.showPage(this.pages.length - 1); }
      }, { signal });

      // Horizontal swipe on touch devices, unless the page is zoomed and being panned.
      let startX = 0; let startY = 0; let tracking = false;
      const stage = this.viewer.querySelector('[data-viewer-stage]');
      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || this.viewerFrame.classList.contains('is-zoomed')) return;
        tracking = true; startX = event.clientX; startY = event.clientY;
      }, { signal });
      stage.addEventListener('pointerup', (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX; const dy = event.clientY - startY;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) this.showPage(this.pageIndex + (dx < 0 ? 1 : -1));
      }, { signal });
      stage.addEventListener('pointercancel', () => { tracking = false; }, { signal });
      this.bindDialogClose(this.viewer);
    }

    showPage(index) {
      if (!this.pages?.length) return;
      index = Math.min(this.pages.length - 1, Math.max(0, index));
      const page = this.pages[index];
      this.pageIndex = index;
      this.viewerFrame.classList.remove('is-zoomed');
      this.viewerFrame.scrollTo({ top: 0 });
      this.viewerImage.src = page.src;
      this.viewerImage.alt = `${page.title}, original catalogue page ${page.label}`;
      this.viewerTitle.textContent = page.title;
      this.viewerCounter.textContent = `${page.label} · ${index + 1} of ${this.pages.length}`;
      this.viewerFull.href = page.src;
      this.viewerPrev.disabled = index === 0;
      this.viewerNext.disabled = index === this.pages.length - 1;
      this.thumbs.forEach((thumb, i) => {
        const near = Math.abs(i - index) < 14;
        const img = thumb.querySelector('img');
        if (near && img.dataset.src && !img.src) img.src = img.dataset.src;
        if (i === index) { thumb.setAttribute('aria-current', 'true'); thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' }); }
        else thumb.removeAttribute('aria-current');
      });
      [index + 1, index - 1].forEach((i) => { if (this.pages[i]) { const pre = new Image(); pre.src = this.pages[i].src; } });
    }

    openDialog(dialog) {
      if (dialog.open) return;
      this.lastFocus = document.activeElement;
      dialog.showModal();
      document.documentElement.classList.add('us-cat-lock');
    }

    closeDialog(dialog) {
      if (!dialog.open) return;
      dialog.close();
    }

    bindDialogClose(dialog) {
      const { signal } = this;
      dialog.querySelectorAll('[data-close-dialog]').forEach((button) => button.addEventListener('click', () => this.closeDialog(dialog), { signal }));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeDialog(dialog);
      }, { signal });
      dialog.addEventListener('close', () => {
        if (![this.sheet, this.viewer].some((d) => d && d.open)) document.documentElement.classList.remove('us-cat-lock');
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus({ preventScroll: true });
      }, { signal });
    }

    revealHash() {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (!id) return;
      const chapter = this.chapters.find((item) => item.id === id);
      if (!chapter) return;
      if (chapter.hidden) this.resetFilters();
      chapter.classList.add('is-in');
      chapter.scrollIntoView({ block: 'start', behavior: 'instant' });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.revealObserver?.disconnect();
      this.spyObserver?.disconnect();
      document.documentElement.classList.remove('us-cat-lock');
      this.controller = null;
    }
  });
})();
